import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Modal, Space, Typography, message } from 'antd';
import classNames from 'classnames/bind';

import LOGO from '../../assets/common/logo.png';

import styles from './index.module.scss';
import API, { setAuthToken } from '../../helpers/API';
import PublicAPI from '../../helpers/PublicAPI';
import { getApiErrorMessage } from '../../utils/getApiErrorMessage';
import { getDefaultHomePath } from '../../utils/getDefaultHomePath';

const cx = classNames.bind(styles);

const LoginPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [loginFieldsReadOnly, setLoginFieldsReadOnly] = useState(true);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [isRequestingReset, setIsRequestingReset] = useState(false);

  const doLogin = async (values) => {
    const { id, password } = values;
    const { success, data } = await API.post('/login', {
      id,
      password,
    });

    if (success && data.accessToken) {
      setAuthToken(data.accessToken);
      localStorage.setItem('@h2tms/role', data.role);
      navigate(getDefaultHomePath(data.role), { replace: true });
    }
  };

  const openResetModal = () => {
    resetForm.setFieldsValue({
      id: form.getFieldValue('id'),
    });
    setResetModalOpen(true);
  };

  const enableLoginFields = (event) => {
    const formElement = event?.currentTarget?.form;

    formElement?.querySelectorAll('input[readonly]').forEach((input) => {
      input.readOnly = false;
    });

    setLoginFieldsReadOnly(false);
  };

  const doRequestPasswordReset = async (values) => {
    setIsRequestingReset(true);
    const response = await PublicAPI.post('/password-reset/request', {
      id: values.id?.trim(),
    });
    setIsRequestingReset(false);

    if (!response?.success) {
      message.error(
        getApiErrorMessage(response, '비밀번호 재설정 메일 발송에 실패했습니다.'),
      );
      return;
    }

    message.success(
      response.message || '입력한 정보가 맞다면 비밀번호 재설정 메일을 발송했습니다.',
    );
    setResetModalOpen(false);
    resetForm.resetFields();
  };

  return (
    <div className={cx('container')}>
      <Form
        form={form}
        layout="vertical"
        autoComplete="on"
        onFinish={doLogin}>
        <div className={cx('box')}>
          <img className={cx('logo')} src={LOGO} alt="BTE 로그인" />
          <Space direction="vertical" className={cx('fieldsWrapper')}>
            <Form.Item name="id" label="아이디" rules={[{ required: true, message: '아이디를 입력해주세요' }]}>
              <Input
                name="h2tms-login-id"
                size="large"
                autoComplete="username"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                readOnly={loginFieldsReadOnly}
                onFocus={enableLoginFields}
                onMouseDownCapture={enableLoginFields}
                onPointerDownCapture={enableLoginFields}
                onTouchStartCapture={enableLoginFields}
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="비밀번호"
              rules={[{ required: true, message: '비밀번호를 입력해주세요' }]}>
              <Input
                name="h2tms-login-password"
                type="password"
                size="large"
                autoComplete="current-password"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                readOnly={loginFieldsReadOnly}
                onFocus={enableLoginFields}
                onMouseDownCapture={enableLoginFields}
                onPointerDownCapture={enableLoginFields}
                onTouchStartCapture={enableLoginFields}
              />
            </Form.Item>
            <button type="button" className={cx('resetTrigger')} onClick={openResetModal}>
              비밀번호 재설정
            </button>
            <Button htmlType="submit" type="primary" block size="large">
              Login
            </Button>
          </Space>
        </div>
      </Form>

      <Modal
        title="비밀번호 재설정"
        open={resetModalOpen}
        onCancel={() => setResetModalOpen(false)}
        onOk={() => resetForm.submit()}
        okText="메일 발송"
        cancelText="취소"
        okButtonProps={{ loading: isRequestingReset }}
        destroyOnClose>
        <Form form={resetForm} layout="vertical" onFinish={doRequestPasswordReset}>
          <Typography.Paragraph className={cx('resetDescription')}>
            아이디 기준으로 등록된 이메일에 비밀번호 재설정 링크를 발송합니다.
          </Typography.Paragraph>
          <Form.Item
            name="id"
            label="아이디"
            rules={[{ required: true, message: '아이디를 입력해주세요.' }]}>
            <Input
              size="large"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LoginPage;
